import {
  defaultInsightSlots,
  resolveInsightSlots,
} from "@redotech/redo-support-ai-common/insights/qualify-support-insights";
import { defaultInsightLocations } from "@redotech/redo-support-ai-common/insights/support-insight-locations";
import {
  SUPPORT_INSIGHT_METRIC_SLOT_COUNT,
  SUPPORT_INSIGHT_SLOT_COUNT,
  SupportInsightActionId,
  SupportInsightMetricLocationId,
  SupportInsightSlot,
  teamsPageBannerLocationId,
} from "@redotech/redo-support-settings-common/support-insight-slots";
import { input } from "@redotech/ui/form";
import { arrayEqual, identityEqual, objectEqual } from "@redotech/util/equal";

export interface SupportInsightConfiguration {
  actions: (SupportInsightActionId | null)[];
  locations: SupportInsightMetricLocationId[];
  /** One flag per spot. Absent or true means the spot is shown. */
  enabled: boolean[];
}

const metricLocationIds = new Set<string>(SupportInsightMetricLocationId);

function isSupportInsightActionId(
  value: string,
): value is SupportInsightActionId {
  for (const id of SupportInsightActionId) {
    if (id === value) {
      return true;
    }
  }
  return false;
}

function isMetricLocationId(
  value: string,
): value is SupportInsightMetricLocationId {
  return metricLocationIds.has(value);
}

/** Saved slots, or the default picks when nothing valid is stored. */
export function supportInsightConfigurationFromSlots(
  slots: readonly SupportInsightSlot[] | null | undefined,
  qualified: readonly SupportInsightActionId[],
): SupportInsightConfiguration {
  const defaults: SupportInsightConfiguration = {
    actions: defaultInsightSlots(qualified),
    locations: defaultInsightLocations(),
    enabled: Array.from({ length: SUPPORT_INSIGHT_SLOT_COUNT }, () => true),
  };
  if (!slots || slots.length !== SUPPORT_INSIGHT_SLOT_COUNT) {
    return defaults;
  }
  const storedLocations = slots
    .slice(0, SUPPORT_INSIGHT_METRIC_SLOT_COUNT)
    .map((slot) => slot.location);
  const banner = slots[SUPPORT_INSIGHT_METRIC_SLOT_COUNT];
  if (
    storedLocations.length !== SUPPORT_INSIGHT_METRIC_SLOT_COUNT ||
    !storedLocations.every(isMetricLocationId) ||
    new Set(storedLocations).size !== storedLocations.length ||
    banner?.location !== teamsPageBannerLocationId
  ) {
    return defaults;
  }
  const actions = slots.map((slot) =>
    slot.action && isSupportInsightActionId(slot.action) ? slot.action : null,
  );
  return {
    actions: resolveInsightSlots(qualified, actions),
    locations: storedLocations,
    enabled: slots.map((slot) => slot.enabled !== false),
  };
}

/** Drops empty actions so the schema stores only a chosen insight. */
export function supportInsightSlotsFromConfiguration(
  configuration: SupportInsightConfiguration,
): SupportInsightSlot[] {
  return Array.from({ length: SUPPORT_INSIGHT_SLOT_COUNT }, (_, index) => {
    const location =
      index === SUPPORT_INSIGHT_METRIC_SLOT_COUNT
        ? teamsPageBannerLocationId
        : configuration.locations[index];
    if (!location) {
      throw new Error("Insight location is missing");
    }
    const action = configuration.actions[index] ?? null;
    const slot: SupportInsightSlot = action
      ? { action, location }
      : { location };
    if (configuration.enabled[index] === false) {
      slot.enabled = false;
    }
    return slot;
  });
}

export const supportInsightConfigurationsEqual =
  objectEqual<SupportInsightConfiguration>({
    actions: arrayEqual(identityEqual),
    locations: arrayEqual(identityEqual),
    enabled: arrayEqual(identityEqual),
  });

export const insightConfigurationForm = input<SupportInsightConfiguration>({
  equal: supportInsightConfigurationsEqual,
});
